import React, {useEffect, useState} from 'react';
import {r} from "@/utils/responsive";
import {motion} from "motion/react"
import masterBotTipAvatar from "@/assets/img/masterbot_avatar_tip.png"
import awardGradient from "@/assets/img/award_gradient.png"
import gridPattern from "@/assets/img/grid_pattern.svg"
import ButtonSpan from "@/components/ButtonSpan";
import ShortcutText from "@/components/ShortcutText";
import ButtonMain from "@/components/ButtonMain";
import buttonAwardNext from "@/assets/img/award_button.png"
import {TRANSITIONS} from "@/config/animations";
import {awardEventActions, type AwardEventData} from "@/events/awardEvents";
import {progressActions} from "@/stores/progressStore";
import {useProgress} from "@/hooks/useProgress";
import {startSectionTransition} from "@/stores/navigationStore";
import ShieldAwardIcon from "@/assets/img/icon_shield.png";
import GuardAwardIcon from "@/assets/img/icon_guard.png";
import PrintAwardIcon from "@/assets/img/icon_print.png";
import StorageAwardIcon from "@/assets/img/icon_storage.png";
import VisionAwardIcon from "@/assets/img/icon_vision.png";
import DisabledWrapper from "@/components/DisabledWrapper";
import ShortcutReforge from "@/components/ShortcutReforge";
import {useShortcuts} from "@/hooks/useShortcuts";
import ModalBlurShapes from "@/components/ModalBlurShapes";

interface AwardLayerProps extends React.HTMLAttributes<HTMLDivElement> {
}

const AwardLayer: React.FC<AwardLayerProps> = ({className, ...props}) => {
    const [isShow, setIsShow] = useState<boolean>(false);
    const [awardData, setAwardData] = useState<AwardEventData | null>(null);
    const [isAnimating, setIsAnimating] = useState<boolean>(false);
    const {currentStep} = useProgress();

    // Подписка на события показа/скрытия награды
    useEffect(() => {
        const unsubscribeShow = awardEventActions.onShowAward((data) => {
            setAwardData(data);
            setIsShow(true);
            setIsAnimating(true);
        });

        const unsubscribeHide = awardEventActions.onHideAward(() => {
            setIsShow(false);
        });

        return () => {
            unsubscribeShow();
            unsubscribeHide();
        };
    }, []);

    // Обработчик анимации появления (без автоматической навигации)
    useEffect(() => {
        if (isShow && isAnimating) {
            const timer = setTimeout(() => {
                // Отмечаем что анимация завершена (убираем автоматический переход)
                setIsAnimating(false);
            }, 1000); // Задержка после анимации появления карточек

            return () => clearTimeout(timer);
        }
    }, [isShow, isAnimating]);

    const handleNextLevel = async () => {
        // Переходим к следующему шагу и загружаем новую задачу
        await progressActions.nextStepWithTask();

        // Переходим в briefing только если не завершили игру
        if (currentStep < 5) {
            startSectionTransition('briefing');
        }
        // Если завершили 5-е задание, nextStepWithTask уже перевел на 'end'

        // Очищаем результат валидации при закрытии награды
        progressActions.clearValidationResult();
        awardEventActions.hideAward();
    };

    // При открытии модалки убираем фокус из Monaco Editor
    useEffect(() => {
        if (isShow) {
            // Убираем фокус из Monaco Editor
            const activeElement = document.activeElement as HTMLElement;
            if (activeElement && activeElement.closest('.monaco-editor')) {
                activeElement.blur();
            }
            // Фокусируемся на body чтобы обработчики клавиш работали
            document.body.focus();
        }
    }, [isShow]);

    useShortcuts({
        onEnter: handleNextLevel,
        disabled: !isShow || isAnimating
    });
    return (
        <motion.div
            animate={{
                backgroundColor: isShow ? '#000E33E5' : '#000E3300',
            }}
            initial={{
                backdropFilter: "blur(0px)",
                backgroundColor: '#000E3300',
            }}
            transition={TRANSITIONS.slow}
            style={{
                position: 'fixed',
                inset: 0,
                zIndex: 99999,
                paddingLeft: r.vmax(63),
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                pointerEvents: !isShow ? "none" : "auto",
                userSelect: "none",
            }}
        >
            <div style={{
                display: "flex",
                justifyContent: "center",
                alignItems: "start",
                gap: r.vmax(16),
            }}>
                <motion.div
                    animate={{
                        opacity: isShow ? 1 : 0,
                        y: isShow ? 0 : 40,
                    }}
                    initial={{
                        opacity: 0,
                        y: 40,
                    }}
                    transition={{
                        ...TRANSITIONS.slow,
                        delay: isShow ? 0.2 : 0
                    }}
                    style={{
                        position: "relative",
                        aspectRatio: 863 / 888,
                        backgroundColor: '#272E64',
                        width: r.vmax(863),
                        borderRadius: r.vmax(60),
                        paddingLeft: r.vmax(124),
                        paddingRight: r.vmax(124),
                        paddingTop: r.vmax(97),
                        paddingBottom: r.vmax(104),
                        overflowX: "hidden",
                    }}>
                    <div style={{
                        display: "flex", justifyContent: "center", position: "relative",
                        zIndex: 3,
                        transform: `translateY(${r.vmax(-5)})`
                    }}>
                        <div style={{
                            position: "absolute",
                            top: '50%',
                            left: '50%',
                            transform: "translate(-50%, -50%)",
                            width: r.vmax(214),
                            height: r.vmax(237),
                        }}>
                            <DisabledWrapper style={{
                                overflow: 'visible'
                            }}>
                                <div className="animate-levitate">
                                    <img src={
                                        awardData?.awardIcon === 'guard' ? GuardAwardIcon :
                                            awardData?.awardIcon === 'print' ? PrintAwardIcon :
                                                awardData?.awardIcon === 'vision' ? StorageAwardIcon :
                                                    awardData?.awardIcon === 'storage' ? VisionAwardIcon :
                                                        ShieldAwardIcon
                                    } alt="" style={{
                                        width: '100%',
                                        height: '100%',
                                        objectFit: 'contain',
                                        objectPosition: 'center',
                                    }}/>
                                </div>
                            </DisabledWrapper>
                        </div>
                        <svg width={r.vmax(303)} height={r.vmax(303)} viewBox="0 0 303 303" fill="none"
                             xmlns="http://www.w3.org/2000/svg" overflow="visible">
                            <path
                                d="M151.5 4C177.391 4 202.827 10.8154 225.25 23.7612C247.673 36.7071 266.293 55.3271 279.239 77.75"
                                stroke="#9BFFFF" strokeOpacity={awardData?.level && awardData.level >= 1 ? "1" : "0.2"}
                                strokeWidth={`max(${r.vmax(awardData?.level && awardData.level >= 1 ? 16 : 8)}, ${awardData?.level && awardData.level >= 1 ? 16 : 8}px)`}
                                strokeLinecap="round"
                                strokeLinejoin="round"/>
                            <path
                                d="M291.781 105.92C299.782 130.544 301.16 156.841 295.777 182.167C290.393 207.493 278.439 230.955 261.114 250.197"
                                stroke="#9BFFFF" strokeOpacity={awardData?.level && awardData.level >= 2 ? "1" : "0.2"}
                                strokeWidth={`max(${r.vmax(awardData?.level && awardData.level >= 2 ? 16 : 8)}, ${awardData?.level && awardData.level >= 2 ? 16 : 8}px)`}
                                strokeLinecap="round"
                                strokeLinejoin="round"/>
                            <path
                                d="M238.198 270.83C217.251 286.049 192.668 295.486 166.918 298.192C141.168 300.898 115.159 296.779 91.5062 286.248"
                                stroke="#9BFFFF" strokeOpacity={awardData?.level && awardData.level >= 3 ? "1" : "0.2"}
                                strokeWidth={`max(${r.vmax(awardData?.level && awardData.level >= 3 ? 16 : 8)}, ${awardData?.level && awardData.level >= 3 ? 16 : 8}px)`}
                                strokeLinecap="round"
                                strokeLinejoin="round"/>
                            <path
                                d="M66.8973 272.325C45.6881 257.474 28.7617 237.302 17.8194 213.836C6.87713 190.37 2.30449 164.438 4.5611 138.645"
                                stroke="#9BFFFF" strokeOpacity={awardData?.level && awardData.level >= 4 ? "1" : "0.2"}
                                strokeWidth={`max(${r.vmax(awardData?.level && awardData.level >= 4 ? 16 : 8)}, ${awardData?.level && awardData.level >= 4 ? 16 : 8}px)`}
                                strokeLinecap="round"
                                strokeLinejoin="round"/>
                            <path
                                d="M10.4449 108.375C18.0149 83.6148 31.9691 61.2833 50.9051 43.6253C69.8411 25.9672 93.0916 13.6047 118.32 7.78037"
                                stroke="#9BFFFF" strokeOpacity={awardData?.level && awardData.level >= 5 ? "1" : "0.2"}
                                strokeWidth={`max(${r.vmax(awardData?.level && awardData.level >= 5 ? 16 : 8)}, ${awardData?.level && awardData.level >= 5 ? 16 : 8}px)`}
                                strokeLinecap="round"
                                strokeLinejoin="round"/>
                        </svg>
                    </div>
                    <div style={{
                        position: "relative",
                        display: "flex",
                        flexDirection: "column",
                        justifyContent: "center",
                        paddingTop: r.vmax(43),
                        paddingBottom: r.vmax(63),
                        color: 'white',
                        textAlign: 'center',
                        fontSize: r.vmax(48),
                        lineHeight: r.vmax(56),
                        letterSpacing: r.vmax(-0.3),
                        zIndex: 3,
                    }}>
                        <h1>{`Уровень ${awardData?.level || 1} из ${awardData?.totalLevels || 5} пройден!`}</h1>
                        <h2>{`Открыта новая суперсила \n«${awardData?.newSuperpower || 'Щит из реплик'}»`}</h2>
                    </div>
                    <div style={{
                        position: "relative",
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        zIndex: 3
                    }}>
                        {awardData?.level === 5 ? (
                            <ButtonMain width={568} height={110} glowWidth={648} glowHeight={190} glowTop={-30}
                                        glowImage={buttonAwardNext}
                                        onClick={async () => {
                                            await progressActions.nextStepWithTask();
                                            progressActions.clearValidationResult();
                                            awardEventActions.hideAward();
                                            startSectionTransition('end');
                                        }}>
                                <ButtonSpan>Завершить восстановление</ButtonSpan>
                                <ShortcutReforge isLarge>ENTER</ShortcutReforge>
                            </ButtonMain>
                        ) : (
                            <ButtonMain width={568} height={110} glowWidth={648} glowHeight={190} glowTop={-30}
                                        glowImage={buttonAwardNext}
                                        onClick={handleNextLevel}>
                                <ButtonSpan>Следующий уровень</ButtonSpan>
                                <ShortcutReforge isLarge>ENTER</ShortcutReforge>
                            </ButtonMain>
                        )}
                    </div>
                    {/*<div style={{*/}
                    {/*    position: 'absolute',*/}
                    {/*    inset: 0,*/}
                    {/*    pointerEvents: 'none',*/}
                    {/*    userSelect: 'none',*/}
                    {/*    zIndex: 2,*/}
                    {/*    mixBlendMode: 'plus-lighter'*/}
                    {/*}}>*/}
                    {/*    <img src={awardGradient} width="100%" height="100%" alt=""/>*/}
                    {/*</div>*/}
                    <ModalBlurShapes shapeColor="#5282FF"/>
                    <div style={{
                        position: 'absolute',
                        inset: 0,
                        pointerEvents: 'none',
                        userSelect: 'none',
                        backgroundImage: `url(${gridPattern})`,
                        backgroundSize: r.vmax(101),
                        backgroundRepeat: 'repeat',
                        backgroundPosition: 'center center',
                        zIndex: 1,
                        opacity: .2,
                        mixBlendMode: 'color-dodge'
                    }}>
                    </div>
                </motion.div>
                <motion.div
                    animate={{
                        opacity: isShow ? 1 : 0,
                        y: isShow ? 0 : 40,
                    }}
                    initial={{
                        opacity: 0,
                        y: 40,
                    }}
                    transition={{
                        ...TRANSITIONS.slow,
                        delay: isShow ? 0.4 : 0
                    }}
                    style={{
                        position: 'relative',
                        backgroundColor: '#272E64',
                        paddingLeft: r.vmax(32),
                        paddingTop: r.vmax(32),
                        paddingRight: r.vmax(32),
                        paddingBottom: r.vmax(60),
                        width: r.vmax(516),
                        borderRadius: r.vmax(60),
                        display: 'flex',
                        flexDirection: 'column',
                        gap: r.vmax(32),
                    }}>
                    <div style={{
                        position: 'relative',
                        width: r.vmax(88),
                        height: r.vmax(88),
                        borderRadius: '50%',
                        pointerEvents: 'none',
                        userSelect: 'none',
                    }}>
                        <div style={{
                            width: r.vmax(181),
                            height: r.vmax(181),
                            position: 'absolute',
                            top: '50%',
                            left: '50%',
                            transform: 'translate(-50%, -50%)'
                        }}>
                            <img src={masterBotTipAvatar} alt="Совет от MasterBot" width="100%" height="100%"/>
                        </div>
                    </div>
                    <div style={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: r.vmax(16),
                        color: 'white',
                        fontSize: r.vmax(24),
                        lineHeight: r.vmax(32),
                        whiteSpace: 'pre-wrap',
                        letterSpacing: r.vmax(-0.3),
                    }}>
                        <p>{`Совет от MasterBot:`}</p>
                        {awardData?.completedTask.masterBotAdvice?.map((hint, i) => (
                            <p>{hint}</p>
                        ))}
                    </div>
                </motion.div>
            </div>
        </motion.div>
    );
};

export default AwardLayer;
